import { FilePdfOutlined, UploadOutlined } from '@ant-design/icons';
import { designTokens } from '@app/theme';
import { parseApiError } from '@shared/lib/api-error';
import { formatDateTime } from '@shared/lib/format-date';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Descriptions, Spin, Tag, Typography, Upload } from 'antd';
import { useTranslation } from 'react-i18next';

import { fetchCurrent, publicFileUrl, publish } from '../api';

import type { DocumentKind, DocumentMeta } from '../api';

const { Text } = Typography;

type Props = {
  kind: DocumentKind;
  /** Заголовок карточки. */
  title: string;
  /** Пояснение, что это за документ. */
  description: string;
  /** Текст подтверждения перед загрузкой; не задан — грузим сразу. */
  confirmText?: string;
  /** Есть ли у администратора право `settings.write` (гейтит кнопку загрузки). */
  canUpload: boolean;
};

/**
 * Карточка одного документа платформы: текущая редакция + загрузка новой.
 *
 * Текущее состояние читается публичным эндпоинтом: метаданные (версия, SHA-256,
 * дата публикации) и ссылка «Открыть PDF»; 404 — бейдж «не опубликован».
 * Загрузка PDF создаёт новую редакцию; для оферты перед загрузкой показывается
 * подтверждение (пользователи будут акцептовать новую версию при следующем входе).
 * После успешной загрузки карточка показывает метаданные опубликованной редакции.
 */
export function DocumentCard({ kind, title, description, confirmText, canUpload }: Props) {
  const { t } = useTranslation(['documents', 'common', 'errors']);
  const { notification, modal } = App.useApp();
  const qc = useQueryClient();

  const queryKey = ['documents', 'current', kind];
  const currentQuery = useQuery({ queryKey, queryFn: () => fetchCurrent(kind) });

  const publishMutation = useMutation({
    mutationFn: (file: File) => publish(kind, file),
    onSuccess: (meta: DocumentMeta) => {
      qc.setQueryData(queryKey, meta);
      notification.success({
        message: t('documents:upload.success', { version: meta.version ?? '?' }),
        placement: 'topRight',
      });
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: unknown; status?: number } };
      const status = e.response?.status;
      const apiErr = parseApiError(e.response?.data, status);
      const code = status === 413 ? 'FILE_TOO_LARGE' : apiErr.code;
      notification.error({
        message: t(`errors:${code}`, { defaultValue: t('errors:UNKNOWN_ERROR') }),
        placement: 'topRight',
      });
    },
  });

  const startUpload = (file: File): void => {
    if (confirmText) {
      modal.confirm({
        title: t('documents:upload.confirmTitle'),
        content: confirmText,
        okText: t('documents:upload.confirmOk'),
        cancelText: t('common:actions.cancel'),
        onOk: () => {
          publishMutation.mutate(file);
        },
      });
      return;
    }
    publishMutation.mutate(file);
  };

  const meta = currentQuery.data;

  return (
    <Card
      title={
        <span>
          <FilePdfOutlined style={{ marginInlineEnd: 8, color: designTokens.navy900 }} />
          {title}
        </span>
      }
      style={{ borderColor: designTokens.slate200 }}
      extra={
        meta ? (
          <a href={publicFileUrl(kind)} target="_blank" rel="noreferrer">
            {t('documents:card.openPdf')}
          </a>
        ) : null
      }
    >
      <Text type="secondary" style={{ display: 'block', marginBlockEnd: 16 }}>
        {description}
      </Text>

      <Spin spinning={currentQuery.isLoading}>
        {meta ? (
          <Descriptions
            column={1}
            size="small"
            style={{ marginBlockEnd: 16 }}
            items={[
              {
                key: 'version',
                label: t('documents:card.version'),
                children: <Tag color="blue">v{meta.version}</Tag>,
              },
              {
                key: 'publishedAt',
                label: t('documents:card.publishedAt'),
                children: formatDateTime(meta.publishedAt),
              },
              {
                key: 'sha256',
                label: t('documents:card.sha256'),
                children: (
                  <Text
                    copyable={{ text: meta.sha256 }}
                    style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 12,
                      wordBreak: 'break-all',
                    }}
                  >
                    {meta.sha256}
                  </Text>
                ),
              },
            ]}
          />
        ) : (
          !currentQuery.isLoading && (
            <div style={{ marginBlockEnd: 16 }}>
              <Tag color="default">{t('documents:card.notPublished')}</Tag>
            </div>
          )
        )}
      </Spin>

      <Upload
        accept=".pdf,application/pdf"
        showUploadList={false}
        disabled={!canUpload || publishMutation.isPending}
        beforeUpload={(file) => {
          startUpload(file);
          return false;
        }}
      >
        <Button
          type="primary"
          icon={<UploadOutlined />}
          loading={publishMutation.isPending}
          disabled={!canUpload}
          title={canUpload ? undefined : t('documents:upload.noPermission')}
        >
          {meta ? t('documents:upload.replace') : t('documents:upload.publish')}
        </Button>
      </Upload>
    </Card>
  );
}
